import { ChangeDetectionStrategy, Component, Input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ItemsEmisionFacadeService } from './items-emision-facade.service';
import { FieldErrorComponent } from '../../../shared/components/field-error/field-error.component';
import { CurrencyFormatPipe } from '../../../pipes/currency-format.pipe';

@Component({
  selector: 'app-items-aire-spot',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    InputNumberModule,
    InputTextModule,
    ButtonModule,
    DatePickerModule,
    TooltipModule,
    SelectModule,
    DialogModule,
    FieldErrorComponent,
    CurrencyFormatPipe
  ],
  templateUrl: './items-aire-spot.component.html',
  styleUrls: ['./items-aire-spot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsAireSpotComponent {
  @Input() readOnly: boolean = false;
  pricingDialogVisible = false;
  selectedTrace: any = null;

  readonly items = computed(() => this.facade.itemsByType().AIRE_SPOT || []);
  readonly diaTipoOptions = [
    { label: 'Lun-Vie', value: 'LUN_VIE' },
    { label: 'Sábado', value: 'SABADO' },
    { label: 'Domingo', value: 'DOMINGO' },
    { label: 'Feriado', value: 'FERIADO' }
  ];
  readonly bonificadoOptions = [
    { label: 'No', value: false },
    { label: 'Sí', value: true }
  ];
  readonly frecuenciaOptions = [
    { label: 'AM', value: 'AM' },
    { label: 'FM', value: 'FM' }
  ];

  constructor(public facade: ItemsEmisionFacadeService) {}

  trackByRowId(index: number, item: { group: FormGroup; index: number }): string | number {
    return item.group.get('clientRowId')?.value ?? item.group.get('id')?.value ?? index;
  }

  handleEnterOnAvisos(event: KeyboardEvent, index: number): void {
    if (event.key !== 'Enter') {
      return;
    }
    if (this.readOnly) {
      return;
    }
    event.preventDefault();
    this.facade.addItem('AIRE_SPOT');
    this.focusPrograma(index + 1);
  }

  openPricingDetail(itemGroup: FormGroup): void {
    this.selectedTrace = itemGroup.get('pricingTrace')?.value ?? null;
    this.pricingDialogVisible = true;
  }

  resetManualSubtotal(itemGroup: FormGroup): void {
    itemGroup.patchValue({ subtotalManual: null });
  }

  handleRowKeydown(event: KeyboardEvent, index: number): void {
    if ((event.ctrlKey || event.altKey) && event.key === 'Enter') {
      if (this.readOnly) {
        return;
      }
      event.preventDefault();
      this.facade.addItem('AIRE_SPOT');
      this.focusPrograma(index + 1);
    }
  }

  handleEnterAdvance(event: Event, itemGroup: FormGroup, controlName: string): void {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') {
      return;
    }
    const control = itemGroup.get(controlName);
    if (control?.invalid) {
      return;
    }
    event.preventDefault();
    this.focusNextField(event);
  }

  confirmRemove(itemGroup: FormGroup, index: number): void {
    const shouldConfirm = this.facade.shouldConfirmDelete(itemGroup, 'AIRE_SPOT');
    if (!shouldConfirm || window.confirm('¿Eliminar ítem de Aire Spot?')) {
      this.facade.removeItem(index);
    }
  }

  focusPrograma(index: number): void {
    setTimeout(() => {
      const input = document.getElementById(`aire-item-programa-${index}`);
      if (input) {
        input.focus();
      }
    }, 0);
  }

  private focusNextField(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const row = target?.closest('.item-row');
    if (!row) {
      return;
    }
    const focusables = Array.from(row.querySelectorAll<HTMLElement>(
      'input,button,textarea,select,[tabindex]:not([tabindex="-1"])'
    ));
    const index = focusables.indexOf(target as HTMLElement);
    const next = focusables[index + 1];
    if (next) {
      next.focus();
    }
  }
}
